import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./lightbox.html";
import { createEvent } from '~/modules/analytics/analyticsHelper';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import webTemplate from './lightbox.html';
import mobileTemplate from './lightbox_mobile.html';
const AUTOPLAY_REMOVAL_REGEX = /(\?|&)autoplay=\d/g;

// focus should always be the first element
const FOCUS_SHIM_INDEX = 0;
const FOCUS_ELEMENTS_SELECTOR = 'video, wds-button, [tabindex]:not([tabindex="-1"]), a[href]:not([disabled]), button:not([aria-disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])';
class LightBox extends LightningElement {
  constructor(...args) {
    super(...args);
    this.i18nModule = void 0;
    this.labels = void 0;
    this.items = void 0;
    this.initialItemPosition = 0;
    this.isMobile = false;
    /* Instrumentation element type */
    this.ga4EventType = '';
    this._currentItem = void 0;
    this._initialRender = true;
  }
  render() {
    return this.isMobile ? mobileTemplate : webTemplate;
  }
  get src() {
    return this._currentItem.src;
  }
  get caption() {
    return this._currentItem.caption;
  }
  get altText() {
    return this._currentItem.altText || this.caption;
  }
  get showVideo() {
    return this._currentItem.type === 'Video';
  }
  get useVideoTag() {
    return this.isViewerVideo();
  }
  get shouldAutoplay() {
    // @ts-expect-error * Automated Type Error Fix
    return this._currentItem.shouldAutoPlay;
  }
  get enableControls() {
    return this.items.length > 1;
  }
  get currentPosition() {
    return this._currentItem.position + 1;
  }
  get totalItems() {
    return this.items.length;
  }
  get pagination() {
    return this.i18nModule.listingDetails.carousel.pagination({
      CURRENT_POSITION: this.currentPosition,
      TOTAL_ITEMS: this.totalItems
    });
  }
  isViewerVideo() {
    return this._currentItem.videoViewerType === 'video';
  }

  // @ts-expect-error * Automated Type Error Fix
  setCurrentItem(position) {
    this._currentItem = this.items.filter(item => item.position === position)[0];
    this.screenshotEvent();
  }
  showNextItem() {
    const currentItemPosition = this._currentItem.position;
    const newPosition = this.isLastItem() ? 0 : currentItemPosition + 1;
    this.removeAutoplay();
    this.setCurrentItem(newPosition);
    this.dispatchGA4Event('next');
  }
  showPreviousItem() {
    const currentItemPosition = this._currentItem.position;
    const newPosition = this.isFirstItem() ? this.lastItemPosition() : currentItemPosition - 1;
    this.removeAutoplay();
    this.setCurrentItem(newPosition);
    this.dispatchGA4Event('back');
  }
  removeAutoplay() {
    // eslint-disable-next-line @lwc/lwc/no-api-reassignments
    this.items = this.items.map(item => {
      return {
        ...item,
        src: item.src.replace(AUTOPLAY_REMOVAL_REGEX, ''),
        shouldAutoPlay: false
      };
    });
  }
  isFirstItem() {
    return this._currentItem.position === 0;
  }
  lastItemPosition() {
    return this.items.length - 1;
  }
  isLastItem() {
    return this._currentItem.position + 1 > this.items.length - 1;
  }
  handleTab(event) {
    this._previousFocusedElement = event.target;
    if (event.shiftKey) {
      if (event.target === this._firstFocusableElement) {
        this._lastFocusableElement.focus();
        event.preventDefault();
      }
    } else {
      if (event.target === this._lastFocusableElement) {
        this._firstFocusableElement.focus();
        event.preventDefault();
      }
    }
  }
  connectedCallback() {
    this._currentItem = this.items.filter(item => item.position === this.initialItemPosition)[0];
    // This will keep the body from scrolling while the modal is open.
    const styles = document.body.getAttribute('style') || '';
    document.body.setAttribute('style', styles.concat(' overflow-y: hidden;'));
  }
  disconnectedCallback() {
    const styles = document.body.getAttribute('style');
    // @ts-expect-error * Automated Type Error Fix
    document.body.setAttribute('style', styles.replace(' overflow-y: hidden;', ''));
  }
  close() {
    this.dispatchEvent(new CustomEvent('close'));
    this.dispatchGA4Event('close');
  }
  renderedCallback() {
    if (this._initialRender) {
      this._initialRender = false;
      this.screenshotEvent();
      const focusableContent = this.getFocusableElements();
      if (focusableContent?.length > 0) {
        this._firstFocusableElement = focusableContent[0]; // get first element to be focused inside modal
        this._lastFocusableElement = focusableContent[focusableContent.length - 1]; // get last element to be focused inside modal
      }
    }
    if (!this._focusElement && !this.enableControls) {
      if (this.showVideo) {
        // @ts-expect-error * Automated Type Error Fix
        this._focusElement = this.isViewerVideo ? 'video' : 'iframe';
      } else {
        this._focusElement = '.close__button';
      }
    }
    if (this._focusElement) {
      this.forceFocus(this._focusElement);
    }
  }
  screenshotEvent() {
    const ev = new CustomEvent('analytics', {
      bubbles: true,
      composed: true,
      detail: {
        elementType: 'image',
        eventType: 'imageShown',
        image: {
          src: this.src,
          positionIndex: this.currentPosition
        }
      }
    });
    this.dispatchEvent(ev);
  }
  handleKeyDown(event) {
    switch (event.key) {
      case 'Tab':
        this.handleTab(event);
        break;
      case 'Escape':
        this.close();
        break;
      case 'ArrowLeft':
        this._focusElement = '.back__button';
        this.showPreviousItem();
        break;
      case 'ArrowRight':
        this._focusElement = '.forward__button';
        this.showNextItem();
        break;
      default:
        break;
    }
  }
  forceFocus(element) {
    this.template.querySelector(element)?.focus();
  }

  /**
   * This function exists because without another focusable element, shift-tab breaks out of the focus-trap
   * The div-shim once be focused, we will force focus on the first or last non-shim item. Previous element is tracked
   * in _previousFocusedElement
   */
  handleModalInteractionLoopFocus(event) {
    const focusableContent = this.getFocusableElements();
    if (focusableContent.length > 1) {
      const firstNonShimItem = focusableContent[FOCUS_SHIM_INDEX + 1];
      const lastItem = focusableContent[focusableContent.length - 1];

      // TAB - if the previously focused item is the last focus item, we should focus on the first item (unless it's the div shim)
      if (this._previousFocusedElement === this._lastFocusableElement) {
        firstNonShimItem.focus();
        event.preventDefault();
      } else if (this._previousFocusedElement === firstNonShimItem) {
        // SHIFT-TAB - if previous focused item was the first (non-shim) item, focus on the last element
        lastItem.focus();
        event.preventDefault();
      }
    }
  }
  getFocusableElements() {
    const modal = this.template.querySelector('[role=dialog]');
    // @ts-expect-error * Automated Type Error Fix
    return modal?.querySelectorAll(FOCUS_ELEMENTS_SELECTOR);
  }
  dispatchGA4Event(label) {
    if (this.ga4EventType) {
      const eventDetail = {
        elementType: this.ga4EventType,
        eventType: 'click',
        label: label
      };
      const ev = createEvent(eventDetail);
      this.dispatchEvent(ev);
    }
  }
  /*LWC compiler v7.1.3*/
}
/* This makes the component focusable */
LightBox.delegatesFocus = true;
_registerDecorators(LightBox, {
  publicProps: {
    items: {
      config: 0
    },
    initialItemPosition: {
      config: 0
    },
    isMobile: {
      config: 0
    },
    ga4EventType: {
      config: 0
    }
  },
  track: {
    _currentItem: 1
  },
  wire: {
    i18nModule: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: () => i18nStore.getState().i18n
        };
      }
    },
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: i18n => i18n.listingDetails.carousel
        };
      }
    }
  },
  fields: ["_initialRender"]
});
const __lwc_component_class_internal = _registerComponent(LightBox, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;